class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

/**
 * <cart-drawer-upsell>: carousel controller for the upsell
 * (snippets/cart-drawer-recommendations.liquid). Cards are loaded per type tab by
 * <theme-cross-sell> (assets/theme-cross-sell.js); this element only drives the
 * visible tab's track: prev/next arrows, a swipe guard (a tap-only gate for older
 * customers) and live variant pricing.
 */
const UPSELL_TAP_MOVE_PX = 8;
const UPSELL_TAP_MAX_MS = 600;
const UPSELL_SCROLL_COOLDOWN_MS = 300;

class CartDrawerUpsell extends HTMLElement {
  constructor() {
    super();
    // Variant <select> changes must not reach cart-drawer-items, whose
    // `change` handler treats every change as a line-quantity update.
    this.addEventListener('change', this.onChange.bind(this));
    // Prev/next arrows (visible controls, not swipe-only).
    this.addEventListener('click', this.onClick.bind(this));
    // Swipe guard.
    this.addEventListener('pointerdown', this.onGestureStart.bind(this), { passive: true });
    this.addEventListener('touchstart', this.onGestureStart.bind(this), { passive: true });
    this.addEventListener('touchmove', this.onTouchMove.bind(this), { passive: true });
    this.addEventListener('click', this.cancelSwipeClick.bind(this), true);
    this.lastScrollAt = 0;
    this.onAnyScroll = () => {
      this.lastScrollAt = Date.now();
      if (this.gesture) this.gesture.scrolled = true;
    };
    // Track scrolls don't bubble; capture them for whichever tab's track is visible.
    this.addEventListener(
      'scroll',
      (event) => {
        if (!event.target.matches?.('[data-upsell-track]')) return;
        this.updateArrows();
        this.onAnyScroll();
      },
      { capture: true, passive: true }
    );
    // Tab switched or cards loaded.
    this.addEventListener('theme-cross-sell:change', () => this.updateArrows());
  }

  connectedCallback() {
    // Vertical scrolling of the items area counts as a swipe too.
    this.scrollParent = this.closest('cart-drawer-items') || this.parentElement;
    this.scrollParent?.addEventListener('scroll', this.onAnyScroll, { passive: true });
    this.updateArrows();
  }

  disconnectedCallback() {
    this.scrollParent?.removeEventListener('scroll', this.onAnyScroll);
  }

  get track() {
    return this.querySelector('[data-upsell-track]:not([hidden])');
  }

  // ---- arrows ----
  onClick(event) {
    const arrow = event.target.closest('[data-upsell-prev], [data-upsell-next]');
    if (!arrow || !this.track) return;
    const card = this.track.querySelector('.cart-upsell__item');
    const step = card ? card.getBoundingClientRect().width + 12 : this.track.clientWidth * 0.8;
    this.track.scrollBy({ left: arrow.hasAttribute('data-upsell-prev') ? -step : step, behavior: 'auto' });
    this.updateArrows();
  }

  updateArrows() {
    const track = this.track;
    if (!track) return;
    const prev = this.querySelector('[data-upsell-prev]');
    const next = this.querySelector('[data-upsell-next]');
    if (!prev || !next) return;
    const max = track.scrollWidth - track.clientWidth;
    prev.toggleAttribute('disabled', track.scrollLeft <= 2);
    next.toggleAttribute('disabled', track.scrollLeft >= max - 2);
  }

  // ---- variant select ----
  onChange(event) {
    event.stopPropagation();
    const select = event.target.closest('.cart-upsell__select');
    if (!select) return;
    const price = select.selectedOptions[0]?.dataset.price;
    const priceElement = select.closest('.cart-upsell__item')?.querySelector('[data-upsell-price]');
    if (price && priceElement) priceElement.textContent = price;
  }

  // ---- swipe guard ----
  onGestureStart(event) {
    if (!event.target.closest('[data-upsell-track]')) {
      this.gesture = null;
      return;
    }
    const point = event.touches ? event.touches[0] : event;
    if (this.gesture && Date.now() - this.gesture.time < 50) return;
    this.gesture = { x: point.clientX, y: point.clientY, time: Date.now(), moved: false, scrolled: false };
  }

  onTouchMove(event) {
    if (!this.gesture) return;
    const point = event.touches[0];
    if (Math.hypot(point.clientX - this.gesture.x, point.clientY - this.gesture.y) > UPSELL_TAP_MOVE_PX) {
      this.gesture.moved = true;
    }
  }

  cancelSwipeClick(event) {
    if (!event.target.closest('[data-upsell-track]')) return;
    const gesture = this.gesture;
    this.gesture = null;
    const now = Date.now();
    const scrolledJustNow = now - this.lastScrollAt < UPSELL_SCROLL_COOLDOWN_MS;
    const movedSinceDown = !!gesture && Math.hypot(event.clientX - gesture.x, event.clientY - gesture.y) > UPSELL_TAP_MOVE_PX;
    const held = !!gesture && now - gesture.time > UPSELL_TAP_MAX_MS;
    const swiped = !!gesture && (gesture.moved || gesture.scrolled);
    if (scrolledJustNow || swiped || held || movedSinceDown) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  }
}

customElements.define('cart-drawer-upsell', CartDrawerUpsell);
